import React, { type ReactNode } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Ionicons, Plus } from './icons';
import { colors, formes, radius, shadow, spacing, typography } from '../theme/tokens';

export interface SectionRepliableProps {
  titre: string;
  ouverte: boolean;
  onToggle: () => void;
  // Nombre d'éléments de la section : le zéro s'affiche, c'est une information.
  compteur?: number;
  // Refonte « Note ton CV » : pastille d'icône à gauche, ligne grise sous le titre, et un « + » qui
  // pivote en croix à la place du chevron. Sans ces trois props, la carte reste celle de l'éditeur.
  icone?: ReactNode;
  sousTitre?: string;
  indicateur?: 'chevron' | 'plus';
  // Garde le contenu monté (mais masqué) quand la section est repliée : l'état des enfants, par
  // exemple une réécriture IA en cours ou proposée, survit au repli au lieu d'être jeté.
  garderMonte?: boolean;
  children: ReactNode;
  testID?: string;
}

/**
 * Carte à en-tête cliquable qui déplie son contenu (éditeur de CV : expériences, formation,
 * compétences…). L'état est tenu par l'appelant : l'écran décide quelles sections s'ouvrent
 * ensemble. Fermée, elle ne monte pas son contenu, ce qui évite de rendre des dizaines de champs
 * invisibles.
 */
export function SectionRepliable({
  titre,
  ouverte,
  onToggle,
  compteur,
  icone,
  sousTitre,
  indicateur = 'chevron',
  garderMonte = false,
  children,
  testID,
}: SectionRepliableProps) {
  return (
    <View testID={testID} style={styles.carte}>
      <Pressable
        testID={testID ? `${testID}-entete` : undefined}
        accessibilityRole="button"
        accessibilityState={{ expanded: ouverte }}
        accessibilityLabel={sousTitre ? `${titre}, ${sousTitre}` : undefined}
        onPress={onToggle}
        style={styles.entete}
      >
        {icone ? <View style={styles.icone}>{icone}</View> : null}
        {sousTitre ? (
          <View style={styles.titres}>
            <Text style={styles.titre}>{titre}</Text>
            <Text style={styles.sousTitre}>{sousTitre}</Text>
          </View>
        ) : (
          <Text style={styles.titre}>{titre}</Text>
        )}
        {compteur !== undefined ? (
          <View testID={testID ? `${testID}-compteur` : undefined} style={styles.compteur}>
            <Text style={styles.compteurTexte}>{compteur}</Text>
          </View>
        ) : null}
        {indicateur === 'plus' ? (
          <View
            testID={testID ? `${testID}-plus` : undefined}
            style={[styles.plus, ouverte && { transform: [{ rotate: '45deg' }] }]}
          >
            <Plus size={14} color={colors.electricInk} strokeWidth={2.6} />
          </View>
        ) : (
          <Ionicons name={ouverte ? 'chevron-up' : 'chevron-down'} size={18} color={colors.grey400} />
        )}
      </Pressable>
      {ouverte || garderMonte ? <View style={[styles.contenu, !ouverte && styles.masque]}>{children}</View> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  carte: {
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  entete: {
    minHeight: formes.hauteur.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  // `flex: 1` : un titre long passe à la ligne au lieu de repousser le compteur hors de la carte.
  titre: {
    flex: 1,
    fontSize: typography.bodyMd.size,
    lineHeight: typography.bodyMd.lineHeight,
    fontWeight: '800',
    color: colors.night,
  },
  compteur: {
    minWidth: spacing.xl,
    height: spacing.xl - spacing.xs,
    paddingHorizontal: spacing.sm - 1,
    borderRadius: radius.pill,
    backgroundColor: colors.grey100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  icone: {
    width: 38,
    height: 38,
    borderRadius: formes.rayon.sm,
    backgroundColor: colors.blueSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titres: { flex: 1, gap: 1 },
  sousTitre: { fontSize: 12, lineHeight: 16, color: colors.grey500 },
  plus: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: colors.blueSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  compteurTexte: { fontSize: typography.caption.size, fontWeight: '800', color: colors.grey500 },
  contenu: { paddingHorizontal: spacing.lg, paddingBottom: spacing.lg, gap: spacing.md },
  masque: { display: 'none' },
});
